import { useCallback } from 'react';
import { ReorderSteps, useReorderStepsCtx } from './ReorderStepsCtx';
import { useSweepCanvasState } from '../internal-context/CanvasStateContext';
import {
  CanvasElementType,
  SweepNodesChangeReorderSteps,
  SweepNodesChangeType,
} from '../canvas-types';
import { uniqueId } from '../../../lib/uniqueId';
import { RFEdgeFloatingEdge } from '../edges';
import { useSweepCanvasFitView } from '../useSweepCanvasFitView';
import {
  RfNodeGroupLabel,
  RfNodeReorderNode,
  SweepCanvasRfNode,
} from '../canvas-types/nodeTypesData';
import { RfNodeGroup } from '../canvas-types/nodeTypesData';
import { createObjectOperations } from '../node-changes-event-handlers/objectOperations';
import { arePositionsEqual } from '../helpers/xYPositionOperation';
import { useSweepCanvasPropsCtx } from '../internal-context/SweepCanvasPropsCtx';
import {
  clearEdgeOperationsHighlight,
  clearNodeOperationsHighlight,
} from '../highlight/useHighlightNodesAndEdges';

export interface ReorderStepsRet {
  activateReorderStepsMode: (params: { stepId: string; groupId: string }) => void;
  deactivateReorderStepsMode: () => void;
  reorderSteps?: ReorderSteps;
  applyReorderSteps: () => void;
}

export const useReorderSteps = (): ReorderStepsRet => {
  const { setReorderSteps, reorderSteps, previousNodes, previousEdges } = useReorderStepsCtx();
  const { setCanvasNodes, setCanvasEdges, getCanvasNodes } = useSweepCanvasState();
  const { onSweepNodesChange, onDeactivateReorderStepsMode } = useSweepCanvasPropsCtx();
  const { fitAroundNodes } = useSweepCanvasFitView();

  const reallyActivateReorderStepsMode = useCallback(
    ({ stepId, groupId }: { stepId: string; groupId: string }) => {
      setReorderSteps({ stepId, groupId });

      setCanvasNodes((nodes, operations) => {
        previousNodes.current = nodes;

        const {
          setObject: setNode,
          addObject: addNode,
          setObjectsBySelector: setNodesBySelector,
          affectAllObjectsWithPartial,
          getObjects: getNodes,
        } = operations;

        affectAllObjectsWithPartial({
          hidden: true,
          selectable: false,
          draggable: false,
        });
        setNodesBySelector(
          (node) => node.id === groupId || node.id === `label-${groupId}`,
          (node) =>
            ({
              ...node,
              hidden: false,
              data: {
                ...node.data,
                isReorderingNodes: true,
              },
            }) as RfNodeGroup | RfNodeGroupLabel,
        );
        getNodes().forEach((node) => {
          if (node.type === CanvasElementType.REGULAR && node.parentId === groupId) {
            addNode({
              id: uniqueId(),
              type: CanvasElementType.DROP_ZONE_NODE,
              parentId: groupId,
              data: {
                objectType: node.data.objectType,
                type: 'reorderSteps',
              },
              position: {
                x: node.position.x,
                y: node.position.y,
              },
              zIndex: -1000,
            });

            addNode({
              id: uniqueId(),
              type: CanvasElementType.REORDER_NODE,
              parentId: groupId,
              data: {
                name: node.data.name,
                objectType: node.data.objectType,
                isNb: node.data.isNb,
                originalNodeId: node.id,
              },
              position: {
                x: node.position.x,
                y: node.position.y,
              },
              zIndex: node.data.isNb ? 9 : 10,
              draggable: !node.data.isNb,
            });
          }
          if ([groupId, `label-${groupId}`].includes(node.id)) {
            setNode(node.id, {
              ...node,
              hidden: false,
            });
          }
        });
        setNode<RfNodeGroup>(groupId, (node) => ({
          ...node,
          data: {
            ...node.data,
            simpleHighlighted: true,
          },
        }));

        return getNodes();
      });

      setCanvasEdges((edges) => {
        previousEdges.current = edges;
        return edges.map((edge) => {
          const isVisible =
            edge.data?.sourceParentId === groupId && edge.data?.targetParentId === groupId;
          return {
            ...edge,
            hidden: !isVisible,
            data: {
              ...edge.data,
              highlightType: undefined,
              simpleHighlighted: false,
              showGates: false,
            },
          } as RFEdgeFloatingEdge;
        });
      });
    },
    [previousEdges, previousNodes, setCanvasEdges, setCanvasNodes, setReorderSteps],
  );

  const activateReorderStepsMode = useCallback(
    ({ stepId, groupId }: { stepId: string; groupId: string }) => {
      const groupNodes = getCanvasNodes()
        .filter(
          (node) => groupId && (node.parentId === groupId || node.parentId === `label-${groupId}`),
        )
        .map((node) => node.id);

      fitAroundNodes({
        nodeIds: groupNodes,
        options: {
          padding: 2,
          minZoom: 0.5,
          duration: 400,
        },
        firstChildNodeOnly: true,
        onAnimationEnd: () => {
          reallyActivateReorderStepsMode({ stepId, groupId });
        },
      });
    },
    [fitAroundNodes, getCanvasNodes, reallyActivateReorderStepsMode],
  );

  const deactivateReorderStepsMode = useCallback(() => {
    setReorderSteps(undefined);
    if (previousNodes.current) {
      const nodeOperations = createObjectOperations<SweepCanvasRfNode>(previousNodes.current);
      setCanvasNodes(clearNodeOperationsHighlight(nodeOperations).getObjects());
      previousNodes.current = undefined;
    }
    if (previousEdges.current) {
      const edgeOperations = createObjectOperations<RFEdgeFloatingEdge>(previousEdges.current);
      setCanvasEdges(clearEdgeOperationsHighlight(edgeOperations).getObjects());
      previousEdges.current = undefined;
    }
    onDeactivateReorderStepsMode?.();
  }, [
    previousEdges,
    previousNodes,
    setCanvasEdges,
    setCanvasNodes,
    setReorderSteps,
    onDeactivateReorderStepsMode,
  ]);

  const applyReorderSteps = useCallback(() => {
    if (!reorderSteps) {
      return;
    }
    const { findObjectsBySelector, findFirstObjectBySelector } =
      createObjectOperations<SweepCanvasRfNode>(getCanvasNodes());

    const reorderedNodes = findObjectsBySelector<RfNodeReorderNode>(
      (node) => node.type === CanvasElementType.REORDER_NODE,
    );

    const changes: SweepNodesChangeReorderSteps = {
      type: SweepNodesChangeType.ReorderSteps,
      change: {
        groupId: reorderSteps.groupId,
        switchStepPairs: [],
      },
    };

    reorderedNodes.forEach((reorderedNode) => {
      const selectNodeInTheSamePosition = (node: SweepCanvasRfNode) =>
        node.parentId === reorderedNode.parentId &&
        node.type === CanvasElementType.REGULAR &&
        arePositionsEqual(node.position, reorderedNode.position);

      const realNodeAtPosition = findFirstObjectBySelector(selectNodeInTheSamePosition);
      if (realNodeAtPosition && realNodeAtPosition.id !== reorderedNode.data.originalNodeId) {
        const switchStepPair: [string, string] = [
          reorderedNode.data.originalNodeId,
          realNodeAtPosition.id,
        ].sort() as [string, string];

        // Verify that the pair is not already in the array
        if (
          !changes.change.switchStepPairs.some(
            (pair) => pair[0] === switchStepPair[0] && pair[1] === switchStepPair[1],
          )
        ) {
          changes.change.switchStepPairs.push(switchStepPair);
        }
      }
    });
    if (changes.change.switchStepPairs.length) {
      onSweepNodesChange?.([changes]);
    }
    deactivateReorderStepsMode();
  }, [deactivateReorderStepsMode, getCanvasNodes, onSweepNodesChange, reorderSteps]);

  return { activateReorderStepsMode, deactivateReorderStepsMode, reorderSteps, applyReorderSteps };
};
